// src/pages/DataframePage.js

/**
 * DataframePage – displays detailed, paginated entities extracted from a single document.
 *  
 * Features:
 * - Reads the selected document and filename from DocumentContext.
 * - All table rendering delegated to TableRouter component.
 * - Maintains expansion state and menu handlers.
 *  
 * Hooks & Helpers:
 * - `toggleRow(rowId, e)`: expand or collapse a row.
 * - `handleMenuOpen/Close`: menu state handlers.
 *  
 * Contexts:
 * - AuthContext.currentUser.uid as userId.
 * - DocumentContext.selectedDocument & documentFilename.
 *  
 * Author: Galo Alonso Peralta Espinoza
 * Date: 04-17-2025
 */

import React, { useState, useEffect, useContext } from 'react'

// Utils
import {
  downloadFiles
} from '../utils/downloadHelpers'

// Context
import { AuthContext } from '../context/AuthContext'
import { DocumentContext } from '../context/DocumentContext'

// Local Components
import Navbar from './components/Navbar'
import TableRouter from './components/TableRouter'
import PaywallModal from './components/PaywallModal'

// Local CSS
import styles from './DataframePage.module.css'

// Authentication helpers
import { authenticatedFetch } from '../utils/authHelpers'

function DataframePage() {
    // Auth context for user ID if needed
    const { currentUser } = useContext(AuthContext)
    const userId = currentUser ? currentUser.uid : null

    // Document context
    const { selectedDocument, documentFilename } = useContext(DocumentContext)

    // State variables
    const [expandedRows, setExpandedRows] = useState([])
    const [error, setError] = useState('')
    const [anchorEl, setAnchorEl] = useState(null)
    const [menuRow, setMenuRow] = useState(null)

    // Subscription State
    const [showPaywallModal, setShowPaywallModal] = useState(false)

    // Checking userID
    useEffect(() => {
        if (!userId) {
            console.warn("DataframePage: No userId provided!");
            return;
        }
        // Simulate Fetching
    }, [userId])

    //## ======================= SUBSCRIPTION CHECK ======================= ##//
    
    // Check subscription status on page load
    useEffect(() => {
        const checkSubscription = async () => {
            try {
                const response = await authenticatedFetch('/api/user/subscription-status');
                
                if (response.ok) {
                    const { subscription } = await response.json();
                    setShowPaywallModal(subscription.status === 'inactive' || !subscription.status);
                } else {
                    // If we can't verify subscription, show modal for safety
                    setShowPaywallModal(true);
                }
            } catch (error) {
                console.error('Subscription check failed:', error);
                // Show modal if subscription check fails
                setShowPaywallModal(true);
            }
        };

        checkSubscription();
    }, []);

    //** Collapsible Table Logic **//
    const toggleRow = (rowId, e) => {
        e && e.stopPropagation()
        if (expandedRows.includes(rowId)) {
            setExpandedRows(expandedRows.filter((id) => id !== rowId))
        } else {
            setExpandedRows([...expandedRows, rowId])
        }
    }

    const handleMenuOpen = (e, rowId) => {
        e.stopPropagation()
        setAnchorEl(e.currentTarget)
        setMenuRow(rowId)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        setMenuRow(null)
    }

    // Determine model type from selected document
    const getModelType = () => {
      if (!selectedDocument) return null // No document selected
      return selectedDocument.model || null // Return actual model or null if no model
    }

    return (
        <main className={styles.dataframePage}>
          {/* Navbar Import */}
          <Navbar />
    
          <section className={styles.mainContent}>
            {/* TableRouter Component - handles all table rendering */}
            <TableRouter
              model={getModelType()}
              selectedDocument={selectedDocument}
              documentFilename={documentFilename}
              expandedRows={expandedRows}
              toggleRow={toggleRow}
              anchorEl={anchorEl}
              handleMenuOpen={handleMenuOpen}
              handleMenuClose={handleMenuClose}
              downloadFiles={downloadFiles}
            />
              
            {/* Error */}
            {error && <p style={{ color: 'red' }}>{error}</p>}
          </section>
          
          {/* Paywall Modal - Shows for unsubscribed users */}
          {showPaywallModal && <PaywallModal />}
        </main>
    )
}

export default DataframePage